import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Gender from "constants/type/user/Gender";
import TopBar from "components/common/TopBar";
import PatientService from "modules/patient/service/PatientService";
import {
  formatAgeLabel,
  formatLongDate,
} from "modules/patient/helper/patientFormatters";
import { profileTypeShortLabel } from "modules/patient/helper/patientConstants";
import {
  buildDocUrl,
  openDocument,
} from "modules/patient/helper/documentConstants";
import useRescheduleFlow from "modules/patient/components/useRescheduleFlow";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobileTopBar from "mobile/components/MobileTopBar";
import "modules/patient/styles/updateBookingFor.scss";
import AppointmentService from "../service/AppointmentService";
import useStatusChange from "../hooks/useStatusChange";
import StatusSelect from "./StatusSelect";
import PaymentNote from "./PaymentNote";
import {
  END_STATUSES,
  STATUS_LABEL,
  STATUS_TONE,
  canReschedule,
} from "../helper/appointmentConstants";
import { formatTimeSlot } from "../helper/formatters";
import "../styles/appointmentDetails.scss";

const DASH = "—";

const IST = "Asia/Kolkata";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
/**
 * "5 Feb 2026 11:00 AM" — the board's document meta line carries a TIME, which
 * formatDocDate cannot produce. Additive on purpose: formatDocDate is date-only
 * and the patient header renders it, so it is left alone.
 *
 * Pinned to IST for the same reason the health plan's stamps are: otherwise the
 * same upload reads as a different hour depending on who opens it, with nothing
 * on screen to say which zone was meant.
 */
const formatDocDateTime = (input) => {
  if (input === null || input === undefined || input === "") return "";
  let ms = typeof input === "string" && /^\d+$/.test(input) ? Number(input) : input;
  if (typeof ms === "number" && ms < 1e11) ms *= 1000; // 10-digit seconds
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, day: "2-digit", month: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hourCycle: "h12",
  }).formatToParts(d);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
  const month = MONTHS[parseInt(get("month"), 10) - 1];
  return `${parseInt(get("day"), 10)} ${month} ${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod").toUpperCase()}`;
};

/** Answered → green, Missed → red, anything else → plain. Bridge's mapping.
    The wire sends these LOWERCASE — models.SparkTgEvent documents the field as
    `"answered" or "missed"` — so the map is keyed lowercase and the label is
    capitalised for display, which is how the board draws it. */
const DISPOSITION_TONE = { answered: "ok", missed: "bad" };
const titleCase = (s) =>
  !s ? s : String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase();
/** Render a value, falling back to an em-dash when it's blank/absent. */
const val = (v) => {
  if (v === 0) return "0";
  if (v === null || v === undefined || v === "") return DASH;
  return v;
};

// Server Gender enum is FEMALE=1, MALE=2 — build the map from the constant so it
// can't drift (writing {1:"Male",2:"Female"} by hand inverts every patient's sex).
const GENDER_WORD = { [Gender.MALE]: "Male", [Gender.FEMALE]: "Female" };

/** A read pathway that tolerates the several field names a payload might use. */
const pick = (obj, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj && obj[keys[i]];
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return undefined;
};

/** One label→value pair inside a details card. */
const Field = ({ label, children, wide }) => (
  <div className={`ad-field${wide ? " is-wide" : ""}`}>
    <span className="ad-field-label">{label}</span>
    <div className="ad-field-value">{children}</div>
  </div>
);
Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  wide: PropTypes.bool,
};

/** A titled card holding a grid of fields. */
const Card = ({ title, action, children, className }) => (
  <section className={`ad-card${className ? " " + className : ""}`}>
    <div className="ad-card-head">
      <h2 className="ad-card-title">{title}</h2>
      {action}
    </div>
    <div className="ad-grid">{children}</div>
  </section>
);
Card.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

/* Inline glyphs. Generic shapes (play, download, refresh, document) rather
   than brand marks. */
const IconDoc = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11.5 2.5H6a1.5 1.5 0 00-1.5 1.5v12A1.5 1.5 0 006 17.5h8a1.5 1.5 0 001.5-1.5V6.5z" />
    <path d="M11.5 2.5v4h4M7.5 11h5M7.5 14h5" />
  </svg>
);
const IconImage = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2" />
    <circle cx="7.25" cy="8" r="1.25" />
    <path d="M3.5 14l3.75-3.5 2.5 2.25 3-2.75 3.75 3.5" />
  </svg>
);
const IconDownload = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3.5v9M6.5 9.5l3.5 3.5 3.5-3.5M4 16h12" />
  </svg>
);
const IconPlay = () => (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M3.5 2.2v7.6a.5.5 0 00.77.42l5.9-3.8a.5.5 0 000-.84l-5.9-3.8a.5.5 0 00-.77.42z" />
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 6.5A6.5 6.5 0 104.2 12" />
    <path d="M16.5 2.8v3.9h-3.9" />
  </svg>
);
/** The server's document-format enum, as bridge's getDocumentFormat sends it. */
const DOC_FORMAT = { jpg: 1, jpeg: 1, png: 2, gif: 3, pdf: 4, mp3: 5, mp4: 6 };
const documentFormat = (fileName) =>
  DOC_FORMAT[String(fileName || "").split(".").pop().toLowerCase()] || 0;

/** png/jpg/gif get the picture glyph; everything else the document one. */
const isImageDoc = (d) =>
  /\.(png|jpe?g|gif|webp|heic)$/i.test(String((d && (d.title || d.name)) || "")) ||
  /^(png|jpe?g|gif|webp|heic)$/i.test(String((d && d.format) || ""));

const AppointmentDetails = ({ match, history }) => {
  const appointmentId = Number(match.params.appointmentId);
  const appointmentService = useRef(new AppointmentService()).current;
  const patientService = useRef(new PatientService()).current;

  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The audit trail backs BOTH the Appointment history table and the strip's
  // reschedule count — the filter endpoint carries neither.
  const [audits, setAudits] = useState([]);
  const [auditReschedules, setAuditReschedules] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Signed recording URLs are ~15-minute-lived, so they are fetched per click
  // and cached by recording id: { [id]: "loading" | url }.
  const [recordings, setRecordings] = useState({});
  const historyRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const showError = useCallback(
    (message) => EventService.emit("showError", { message }),
    []
  );

  const loadAppointment = useCallback(() => {
    appointmentService.fetchAppointmentDetail(
      appointmentId,
      (res) => {
        const found = res && res.appointments && res.appointments[0];
        if (!found) {
          setError("We couldn't find that appointment.");
          setLoading(false);
          return;
        }
        setAppointment(found);
        setLoading(false);
        // The patient record fills gender/age/email/type the appointment row
        // doesn't carry. Silent — the fields just show an em-dash on failure.
        /* The MEMBER on a family booking, matching the id this screen already
           prints (`patientIdShown` below resolves bookedFor). Fetching the
           account holder here put a child's patient id beside a parent's age,
           gender and user type on the same card — the record wins those
           tiebreaks, so the mismatch was invisible in the code and obvious on
           screen. */
        /* "Update booking for" is IGNORED on this screen (Nittin, 2026-09-12):
           this card describes the appointment AS BOOKED, the way bridge's own
           appointment-details screen does — it derives the same field as
           `bookedFor == 0 ? patientID : bookedFor` and consults no mapping at
           all. So every value here — the id, the age, the gender, the user type,
           the phonebook — belongs to the member the appointment was booked for. */
        const pid = found.bookedFor && found.bookedFor !== 0 ? found.bookedFor : found.patientID;
        if (pid) {
          patientService.fetchPatient(
            pid,
            (p) => p && p.id && setPatient(p),
            () => {}
          );
        }
      },
      (err) => {
        setError(`We couldn't load that appointment. ${(err && err.message) || ""}`);
        setLoading(false);
      }
    );
  }, [appointmentId, appointmentService, patientService]);

  const loadAudit = useCallback(() => {
    appointmentService.getAppointmentAudit(
      appointmentId,
      (res) => {
        setAudits((res && res.appointmentAudits) || []);
        const n = Number(res && res.rescheduleCount);
        setAuditReschedules(isNaN(n) ? null : n);
      },
      // Silent: the table keeps its empty state. This endpoint is not in the
      // service repo checked out here, only in the deployed estate bridge ships
      // against, so a 404 in some environment must not break the screen.
      () => {}
    );
  }, [appointmentId, appointmentService]);

  /* After a reschedule — bridge's onRefreshStatus: re-read the appointment and
     its audit, and show only what the server returns (no optimistic merge). A
     failed re-read TOASTS and keeps the page, as bridge's fetchAppointment
     does; the first load's full-page error is not for this. */
  const refreshAfterReschedule = useCallback(() => {
    appointmentService.fetchAppointmentDetail(
      appointmentId,
      (res) => {
        const found = res && res.appointments && res.appointments[0];
        if (!found) return;
        setAppointment(found);
        /* "Update booking for" is IGNORED on this screen (Nittin, 2026-09-12):
           this card describes the appointment AS BOOKED, the way bridge's own
           appointment-details screen does — it derives the same field as
           `bookedFor == 0 ? patientID : bookedFor` and consults no mapping at
           all. So every value here — the id, the age, the gender, the user type,
           the phonebook — belongs to the member the appointment was booked for. */
        const pid = found.bookedFor && found.bookedFor !== 0 ? found.bookedFor : found.patientID;
        if (pid) {
          patientService.fetchPatient(
            pid,
            (p) => p && p.id && setPatient(p),
            () => {}
          );
        }
      },
      (err) => showError(`Error: ${err === null || err === undefined ? undefined : err.message}`)
    );
    loadAudit();
  }, [appointmentId, appointmentService, patientService, showError, loadAudit]);

  const { startReschedule, rescheduleUI } = useRescheduleFlow({
    appointment,
    onRescheduled: refreshAfterReschedule,
  });

  useEffect(() => {
    if (!appointmentId) {
      setError("That appointment link looks wrong.");
      setLoading(false);
      return;
    }
    loadAppointment();
    appointmentService.getReasonsForConsultation(
      (res) => setReasons(Array.isArray(res) ? res : (res && res.reasons) || []),
      () => {}
    );
    appointmentService.getAppointmentDocMapping(
      appointmentId,
      (res) => setDocuments((res && res.documents) || []),
      () => {}
    );
    loadAudit();
    // Mint the document token in the background. buildDocUrl appends it, and
    // without it every "View document" (and the new download) opens an
    // untokenised URL the file host rejects — a latent bug on this route,
    // since only DocumentsMenu was minting it.
    patientService.generateDocToken(
      () => {},
      () => {}
    );
  }, [appointmentId, appointmentService, patientService, loadAppointment, loadAudit]);

  /**
   * Upload a report — bridge's handleUploadFile, step for step.
   *
   * Two calls per file, not one: the multipart POST stores the document, and
   * only the follow-up mapping POST attaches it to this appointment. Skipping
   * the second leaves an orphan the screen never shows.
   *
   * THE DOUBLE UPLOAD is bridge's behaviour and is reproduced deliberately.
   * When the appointment was booked FOR someone other than the account holder
   * (bookedFor ≠ 0) the same file is uploaded twice, once against each patient
   * id. Note the type codes end up crossed — `body` keeps type 11 and goes to
   * bookedFor, while `document` is mutated to 6 and goes to patientID. That
   * reads like a slip in bridge, but it is what is deployed, and guessing the
   * other way round would file reports under the wrong type.
   */
  const handleUploadFile = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      setUploading(true);

      const appt = appointment || {};
      const finish = () => {
        setUploading(false);
        appointmentService.getAppointmentDocMapping(
          appt.id,
          (res) => setDocuments((res && res.documents) || []),
          () => {}
        );
      };
      const fail = (err) => {
        setUploading(false);
        showError(`Could not upload that document. ${(err && err.message) || ""}`);
      };
      /* Fire-and-forget, exactly as bridge does — a failure here must not
         surface as an upload failure, because the upload succeeded. */
      const labtest = (res) =>
        appointmentService.labtestAutomation(
          { patientId: res && res.patientId, documentURL: res && res.url },
          () => {},
          () => {}
        );
      const attach = (res, done) =>
        appointmentService.mapDocToAppointment(
          { appointmentId: appt.id, documentId: res && res.id },
          done,
          fail
        );

      const doc = {
        title: "",
        description: "",
        url: "",
        type: 11,
        format: documentFormat(file.name),
        status: 4,
      };

      if (appt.bookedFor && appt.bookedFor !== 0) {
        const body = { ...doc };
        doc.type = 6;
        appointmentService.uploadDocument(
          file,
          file.name,
          body,
          (res) => {
            attach(res, () => {});
            labtest(res);
          },
          fail,
          appt.bookedFor
        );
      }

      appointmentService.uploadDocument(
        file,
        file.name,
        doc,
        (res) => {
          attach(res, finish);
          labtest(res);
        },
        fail,
        appt.patientID
      );

      // Let the same file be chosen again after a failure.
      e.target.value = "";
    },
    [appointment, appointmentService, showError]
  );

  /** Bridge's downloadDocument: pull the tokenised URL as a blob so the file
      saves under its own name instead of navigating the tab. */
  const downloadDocument = useCallback(
    (d) => {
      const url = buildDocUrl(d && d.url);
      if (!url) return;
      fetch(url)
        .then((r) => r.blob())
        .then((blob) => {
          const href = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = href;
          link.download = d.title || d.name || "document";
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(href);
        })
        .catch(() => showError("Could not download that document."));
    },
    [showError]
  );

  /** The refresh beside Call history. Call events ride on the appointment, so
      this refetches that — there is no separate call-history endpoint. */
  const refreshCalls = useCallback(() => {
    if (refreshing) return;
    setRefreshing(true);
    loadAppointment();
    loadAudit();
    window.setTimeout(() => setRefreshing(false), 600);
  }, [refreshing, loadAppointment, loadAudit]);

  /** Fetch a signed URL on demand and swap the cell to a player. */
  const playRecording = useCallback(
    (recordingId) => {
      if (!recordingId) return;
      if (recordings[recordingId]) return; // cached or already in flight
      setRecordings((prev) => ({ ...prev, [recordingId]: "loading" }));
      appointmentService.getCallRecordingSignedUrl(
        recordingId,
        (res) => {
          const url = res && res.url;
          if (url) setRecordings((prev) => ({ ...prev, [recordingId]: url }));
          else
            setRecordings((prev) => {
              const next = { ...prev };
              delete next[recordingId];
              return next;
            });
        },
        () => {
          setRecordings((prev) => {
            const next = { ...prev };
            delete next[recordingId];
            return next;
          });
          showError("Could not load that recording.");
        }
      );
    },
    [recordings, appointmentService, showError]
  );

  const goBack = useCallback(() => history.goBack(), [history]);
  const goToDashboard = useCallback(() => history.push("/navigo-module"), [history]);
  const crumbs = [
    { label: "My appointments", onClick: goToDashboard },
    { label: "Appointment details" },
  ];

  /* THE SHARED FLOW — identical to the dashboard's: a cancel-family status asks
     for a reason, then posts with no refundRequest, leaving refunds to the
     server exactly as the expert dashboard does (see hooks/useStatusChange). */
  const { changeStatus: runStatusChange, updatingId, dialogs } = useStatusChange({
    /* The call note the hook opens after a change is about a person: this
       screen has already read them, so it hands them over rather than letting
       the hook re-derive an id from the appointment. */
    patient,
    onApplied: (status) => {
      /* Patch first so the pill turns over immediately, then re-read.
         THE AUDIT RELOAD IS THE POINT: a status change writes an audit entry,
         and this screen's Appointment history table renders that audit — so
         without it the operator sees the new status in the strip and a history
         that does not mention it. Bridge does the same on this screen, with the
         comment "a status change adds an audit entry, so keep the history table
         in sync". It only started mattering here when that table was wired to
         the live endpoint; before that it was a permanent empty state. */
      setAppointment((prev) => ({ ...prev, status }));
      loadAppointment();
      loadAudit();
    },
    onError: showError,
  });
  const updating = updatingId != null;
  const changeStatus = (status) => runStatusChange(appointment, status);

  /* MOBILE ONLY. This screen keeps its desktop body — already a stack of
     label/value cards that reads at 360px — and swaps only the bar above it,
     so it matches the other mobile screens: back, the heading, and the overflow
     menu that holds Log out. Breadcrumbs need two lines for these labels and
     leave nowhere to log out from. Declared once and used by all three returns
     below. */
  const bar =
    useIsMobile() && MOBILE.appointmentDetails ? (
      <MobileTopBar title="Appointment details" onBack={goBack} />
    ) : (
      <TopBar crumbs={crumbs} onBack={goBack} />
    );

  if (loading) {
    return (
      <div className="ad-page">
        {bar}
        <div className="ad-message">Loading…</div>
      </div>
    );
  }
  if (error || !appointment) {
    return (
      <div className="ad-page">
        {bar}
        <div className="ad-message is-error">
          <p>{error || "Appointment not found."}</p>
          <button type="button" className="ad-btn ad-btn-ghost" onClick={goBack}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const a = appointment;
  const isTele = /consult/i.test(String(pick(a, ["serviceMode", "modeName"]) || "")) ||
    a.mode === 1 || a.mode === 2; // Tele/Video carry the reason trio
  /* bookedFor when there is one, else the holder — bridge's own derivation for
     this field, and blind to "Update booking for" like the rest of the card. */
  const patientIdShown = a.bookedFor && a.bookedFor !== 0 ? a.bookedFor : a.patientID;
  const recordName = patient ? [patient.firstName, patient.lastName].filter(Boolean).join(" ") : "";
  /* The row's `patientName` — built server-side from bookedFor alone, never
     from updatedBookingFor, and the same field bridge shows here. The record is
     the booked member's too now, so the fallback needs no guard. */
  const requestedBy = pick(a, ["patientName"]) || recordName;
  const age = formatAgeLabel(patient && patient.dateOfBirth, (patient && patient.age) || a.patientAge);
  const genderCode = (patient && patient.gender) || a.patientGender;
  // "(32) Male" — the board parenthesises the age and leaves the gender bare
  // (node 3015:8556).
  const ageGender = [
    age === null || age === undefined || age === "" ? "" : `(${age})`,
    GENDER_WORD[genderCode],
  ]
    .filter(Boolean)
    .join(" ");
  const profileType = (patient && patient.profileType) != null ? patient.profileType : a.patientProfileType;
  /* phr's own five types, in this card's shorter wording - "QA", not "QA user"
     (the field is already labelled "User type"). The screen had
     `=== PREMIUM_USER ? "Premium" : "Standard"`, so every HR representative,
     QA, sensitive and default patient printed one invented word: "Standard" is
     in no map, no board and no payload. An unmapped or absent type now
     em-dashes like every other field on the card. */
  const userType = profileTypeShortLabel(profileType);
  const planLabel = [pick(a, ["planID", "planId"]), pick(a, ["planName"])]
    .filter((x) => x !== undefined && x !== null && x !== "")
    .join(" - ");
  const when = [formatLongDate(a.appointmentDate), formatTimeSlot(a.timeSlot, a.serviceDuration)]
    .filter(Boolean)
    .join(", ");
  const reasonName = (() => {
    const r = reasons.find((x) => x.id === a.reasonId);
    return (r && (r.name || r.reason)) || "";
  })();
  // THE AUDIT RESPONSE IS THE SOURCE. The row keys below never arrive on the
  // filter payload — which is why this strip item has almost certainly never
  // rendered in phr — but they are kept as a secondary pick rather than removed,
  // in case a future payload does carry one.
  const rescheduleCount =
    auditReschedules !== null && auditReschedules !== undefined
      ? auditReschedules
      : pick(a, [
          "rescheduleCount",
          "rescheduledCount",
          "noOfReschedules",
          "timesRescheduled",
        ]);
  // BOTH tables now have real sources. The history comes from the audit
  // endpoint; the call events ride on the appointment itself, because the fetch
  // asks for them with &includeEvents=true. The old code read four/three
  // speculative keys off the appointment row that the filter endpoint has never
  // returned, so both tables were permanently empty.
  // "Last updated: 5 Feb 2026" — the newest timestamp across the documents.
  const lastDocUpdate = (() => {
    const stamps = documents
      .map((d) => d.updatedAt || d.createdAt)
      .filter((x) => x !== null && x !== undefined && x !== "")
      .map((x) => {
        let ms = typeof x === "string" && /^\d+$/.test(x) ? Number(x) : x;
        if (typeof ms === "number" && ms < 1e11) ms *= 1000;
        const t = new Date(ms).getTime();
        return isNaN(t) ? null : t;
      })
      .filter((t) => t !== null);
    if (!stamps.length) return "";
    return formatLongDate(Math.max.apply(null, stamps));
  })();

  const apptHistory = audits;
  const callHistory = (a.callData && a.callData.events) || [];

  return (
    <div className="ad-page">
      {bar}

      <div className="ad-body">
        <div className="ad-heading-row">
          <h1 className="ad-title">Appointment details</h1>
          {/* Bridge's full gate: hidden for gym / blocked / terminal
              appointments (canReschedule), not just end statuses. The click
              runs bridge's profile check first (useRescheduleFlow). */}
          {canReschedule(a) && (
            <button type="button" className="ad-btn ad-btn-primary" onClick={startReschedule}>
              Reschedule
            </button>
          )}
        </div>

        {/* Bridge's payment banner (DocumentUpload.js:2841-2892), in bridge's
            position: under the heading, above the status strip. */}
        <PaymentNote appointment={a} />

        {/* ---- status strip ---- */}
        <section className="ad-strip">
          <div className="ad-strip-item">
            <span className="ad-field-label">Appointment status</span>
            <div className="ad-status">
              <StatusSelect
                value={a.status}
                tone={STATUS_TONE[a.status] || "progress"}
                label={STATUS_LABEL[a.status] || DASH}
                labels={STATUS_LABEL}
                tones={STATUS_TONE}
                disabled={updating}
                readOnly={END_STATUSES.indexOf(a.status) !== -1}
                onChange={changeStatus}
              />
            </div>
          </div>
          <div className="ad-strip-item">
            <span className="ad-field-label">Appointment date &amp; time</span>
            <div className="ad-field-value">{when || DASH}</div>
          </div>
          {/* Only when it HAS been rescheduled. The server always sends the
              count (no omitempty, zero-valued until the first reschedule), so
              `!= null` printed "0 times" on every untouched appointment;
              bridge, which this screen is ported from, gates on > 0. Number()
              because the retained key fallback could yet carry a string, and
              "0" > 0 is false while "0" != null is true. */}
          {Number(rescheduleCount) > 0 && (
            <div className="ad-strip-item">
              <span className="ad-field-label">Rescheduled</span>
              <div className="ad-field-value">
                {/* A link on the board (underlined, orange rule). It scrolls to
                    the Appointment history card, which is where the reschedule
                    entries actually are — bridge's appointmentHistoryRef. */}
                <button
                  type="button"
                  className="ad-link"
                  onClick={() =>
                    historyRef.current &&
                    historyRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {rescheduleCount} {Number(rescheduleCount) === 1 ? "time" : "times"}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ---- detail cards ---- */}
        <div className="ad-cols">
          <Card title="User details">
            {/* Plain text, not a link to the patient screen (Nittin,
                2026-10-05). Orbit only — bridge's appointment details keep
                theirs. */}
            <Field label="Patient ID">{patientIdShown || DASH}</Field>
            <Field label="Booked for">{val(requestedBy)}</Field>
            <Field label="Age and gender">{val(ageGender)}</Field>
            <Field label="User type">{val(userType)}</Field>
            <Field label="Account code">{val(a.accountCode)}</Field>
            <Field label="Account name">{val(a.accountName)}</Field>
            <Field label="Plan" wide>{val(planLabel)}</Field>
          </Card>

          <Card title="Appointment details">
            <Field label="Appointment ID">{a.id}</Field>
            <Field label="External order ID">{val(pick(a, ["externalOrderId", "externalOrderID"]))}</Field>
            <Field label="Appointment booked by">{val(pick(a, ["bookedBy", "bookedByName"]))}</Field>
            <Field label="Creation date">{val(formatLongDate(a.createdAt))}</Field>
            <Field label="Appointment date">{val(formatLongDate(a.appointmentDate))}</Field>
            <Field label="Time slot">{val(formatTimeSlot(a.timeSlot, a.serviceDuration))}</Field>
          </Card>

          <Card title="Service details">
            <Field label="Service ID">{val(pick(a, ["serviceID", "serviceId"]))}</Field>
            <Field label="Service type">{val(pick(a, ["serviceTypeName", "serviceType"]))}</Field>
            <Field label="Service sub type">{val(pick(a, ["serviceSubType", "serviceSubTypeName"]))}</Field>
            {/* The CODE ("ML12"). `serviceCode` on the appointment response —
                AppointmentInfo declares it at models/appointment.go:34, and
                bridge's dashboard renders the same field for its Service code
                column. Its SOURCE is the service record's `name`
                (repository/appointment.go:885 does
                `appointment.ServiceCode = service.Name`), which is why the two
                names get conflated; `serviceName` beside it is the human title.
                No fallback chain — an em-dash says the field was not sent. */}
            <Field label="Service code">{val(a.serviceCode)}</Field>
            {/* Both span the full card width on the board (nodes 3015:8622,
                3015:8625) — a service name is long enough to need it. */}
            <Field label="Service name" wide>{val(a.serviceName)}</Field>
            <Field label="Service mode" wide>
              {val(pick(a, ["serviceMode", "modeName", "mode"]))}
            </Field>
            {isTele && (
              <Field label="Reason for consultation" wide>
                {[reasonName, a.reasonDetails].filter(Boolean).join(" — ") || DASH}
              </Field>
            )}
          </Card>

          <Card title="Service provider details">
            <Field label="Service provider ID">{val(pick(a, ["serviceProviderID", "serviceProviderId"]))}</Field>
            <Field label="Service provider name">{val(pick(a, ["serviceProviderName", "providerName"]))}</Field>
            <Field label="Address" wide>{val(pick(a, ["serviceProviderAddress", "providerAddress", "address"]))}</Field>
          </Card>
        </div>

        {/* ---- payment ---- */}
        {/* ---- documents ---- */}
        <section className="ad-card is-docs">
          <div className="ad-card-head">
            <div className="ad-card-headings">
              <h2 className="ad-card-title">Documents related to appointment</h2>
              <p className="ad-card-sub">Reports, prescriptions, or uploaded files</p>
            </div>
          </div>
          {documents.length === 0 ? (
            <p className="ad-empty">No documents uploaded for this appointment.</p>
          ) : (
            <>
              <div className="ad-docs-meta">
                <span className="ad-docs-count">
                  ✓ {documents.length}{" "}
                  {documents.length === 1 ? "document" : "documents"} uploaded
                </span>
                {lastDocUpdate && (
                  <>
                    <span className="ad-docs-rule" aria-hidden="true" />
                    <span className="ad-docs-updated">
                      Last updated: {lastDocUpdate}
                    </span>
                  </>
                )}
              </div>
              <div className="ad-docs">
                {documents.map((d) => (
                  <div className="ad-doc-row" key={d.id || d.title}>
                    <span className="ad-doc-tile" aria-hidden="true">
                      {isImageDoc(d) ? <IconImage /> : <IconDoc />}
                    </span>
                    <div className="ad-doc-meta">
                      <span className="ad-doc-name">
                        {d.title || d.name || "Untitled"}
                      </span>
                      {/* TWO segments on the board, not one interpunct-joined
                          string — and the first carries a time. */}
                      <span className="ad-doc-subs">
                        <span className="ad-doc-sub">
                          Uploaded on{" "}
                          {formatDocDateTime(d.updatedAt || d.createdAt) || DASH}
                        </span>
                        {/* PRINTED VERBATIM: the server already prefixes this
                            ("Uploaded by <name>", "Uploaded by (id) <name>" for
                            an employee or doctor — controllers/document.go), so
                            labelling it again read "Uploaded by Uploaded by
                            Deepshikha testdata". It is deliberately EMPTY when
                            the uploader cannot be named, and an unnamed
                            uploader gets no segment rather than a bare label. */}
                        {typeof d.uploadedBy === "string" && d.uploadedBy.trim() !== "" && (
                          <span className="ad-doc-sub">{d.uploadedBy.trim()}</span>
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ad-btn ad-btn-view"
                      onClick={() => openDocument(d)}
                    >
                      View document
                    </button>
                    <button
                      type="button"
                      className="ad-doc-dl"
                      onClick={() => downloadDocument(d)}
                      aria-label={`Download ${d.title || d.name || "document"}`}
                    >
                      <IconDownload />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Shown in BOTH states — bridge swaps the label rather than hiding
              the control, so an appointment with no documents still has a way
              to get one. */}
          <div className="ad-upload-row">
            <button
              type="button"
              className="ad-upload"
              disabled={uploading}
              onClick={() =>
                uploadInputRef.current && uploadInputRef.current.click()
              }
            >
              {uploading
                ? "Uploading…"
                : documents.length === 0
                ? "+ Upload document"
                : "+ Upload another document"}
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              hidden
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              onChange={handleUploadFile}
            />
          </div>
        </section>

        {/* ---- appointment history: GET /v1/appointment/audit/{id} ----
            The whole card is drawn only when there ARE audits, as bridge's is
            ("hidden entirely when there are no audits", DocumentUpload.js) —
            an empty card saying there is nothing used to sit here (2026-09-21).
            The strip's "Rescheduled" link already tolerates it being absent. */}
        {apptHistory.length > 0 && (
          <section className="ad-card is-history" ref={historyRef}>
            <div className="ad-card-head">
              <h2 className="ad-card-title">Appointment history</h2>
            </div>
            <div className="ad-table-wrap">
              <div className="ad-table-scroll">
                <table className="ad-table is-history">
                  <thead>
                    <tr>
                      {/* The timeline column's header is deliberately empty */}
                      <th className="ad-th-timeline" aria-label="Step" />
                      <th>Status</th>
                      <th>Updated on</th>
                      <th>Updated by</th>
                      <th>Updated from</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apptHistory.map((h, i) => (
                      <tr key={i}>
                        <td className="ad-td-timeline">
                          <span className="ad-step">{i + 1}</span>
                        </td>
                        <td className="ad-td-status">
                          {val(h.statusName || STATUS_LABEL[h.status] || h.status)}
                        </td>
                        {/* PRE-FORMATTED on the wire ("2 Feb 2026, 03:25 PM").
                            formatLongDate would turn it into "Invalid Date". */}
                        <td>{val(h.updatedOn)}</td>
                        <td>{val(h.updatedBy)}</td>
                        <td>{val(h.updatedFrom)}</td>
                        <td>{val(h.comment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* ---- call history: appointment.callData.events (&includeEvents) ----
            Only for an appointment that carries call events, as bridge's is —
            its Refresh with it (bridge's lives inside the same card). */}
        {callHistory.length > 0 && (
          <section className="ad-card is-calls">
            <div className="ad-card-head">
              <h2 className="ad-card-title">Call history</h2>
              <button
                type="button"
                className={`ad-refresh${refreshing ? " is-busy" : ""}`}
                onClick={refreshCalls}
                disabled={refreshing}
                aria-label="Refresh call history"
                title="Refresh call history"
              >
                <IconRefresh />
              </button>
            </div>
            <div className="ad-table-wrap">
              <div className="ad-table-scroll">
                <table className="ad-table is-calls">
                  <thead>
                    <tr>
                      <th>Disposition</th>
                      <th>Start time</th>
                      <th>Handle time</th>
                      <th>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callHistory.map((c, i) => {
                      /* KEBAB-CASE on the wire — not camelCase. */
                      const recId = c["recording-id"];
                      const disposition = c.disposition || "";
                      const tone = DISPOSITION_TONE[String(disposition).toLowerCase()];
                      const state = recId ? recordings[recId] : undefined;
                      return (
                        <tr key={c.callid || i}>
                          <td>
                            <span className={`ad-disp${tone ? " is-" + tone : ""}`}>
                              {tone && <span className="ad-disp-dot" aria-hidden="true" />}
                              {val(titleCase(disposition))}
                            </span>
                          </td>
                          <td>{val(c["start-time"])}</td>
                          <td>{val(c["handle-time"])}</td>
                          <td>
                            {!recId ? (
                              <span className="ad-norec">No recording</span>
                            ) : state && state !== "loading" ? (
                              /* eslint-disable-next-line jsx-a11y/media-has-caption */
                              <audio className="ad-audio" controls autoPlay src={state} />
                            ) : (
                              <button
                                type="button"
                                className="ad-play"
                                onClick={() => playRecording(recId)}
                                disabled={state === "loading"}
                              >
                                {state === "loading" ? (
                                  "Loading…"
                                ) : (
                                  <>
                                    <IconPlay />
                                    Play recording
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* The shared cancel dialog — same sequence as the dashboard. */}
      {dialogs}

      {rescheduleUI}
    </div>
  );
};

AppointmentDetails.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default AppointmentDetails;
